import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { NucleoFeedbackDialogueThinkSpeech, StarAiFill } from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import { Box, Button, Stack, Textarea, Tooltip, Typography } from '@lyearn/ui';
import Typer from '@/components/LyearnAI/components/Typer';
import { Modal } from '@/components/Modals';
import { ModalProps, ModalVariant } from '@/components/Modals/types';
import RegenerationWindow from '@/modules/productivity/views/builder/components/RegenerationWindow';

import SlideDeckUpload from './components/SlideDeckUpload';
import GenerateScriptWindow from './GenerateScriptWindow';
import GenerateVideoForm, {
  GenerateVideoFormValues,
  resolveGenerateVideoFormValues,
} from './GenerateVideoFrom';

const styleAsIconButton = css`
  min-width: 0 !important;
  padding-inline: 0.6rem !important;
  & .MuiButton-startIcon {
    margin: 0;
  }
`;

interface GenerateVideoModalProps {
  blockId?: string;
  videoScript: string;
  onChangeVideoScript: (videoScript: string) => void;
  onConfigChange: (config: {
    avatarId: string;
    voiceId: string;
    videoType: GenerateVideoFormValues['videoType'];
  }) => void;
  onGenerateVideoScript: (userPrompt?: string) => void;
  onRegenerateVideoScript: (userPrompt?: string) => void;
  generatedVideoScript: string | null | undefined;
  isGeneratingVideoScript: boolean;
  isSelectingSlides: boolean;
  onClose: () => void;
  onGenerateStoryboard: () => void;
  initialConfigValues?: Partial<GenerateVideoFormValues>;
  /** Whether the "Describe your video" popover should auto-open on mount */
  shouldAutoOpenScriptPrompt?: boolean;
  /** Override modal size */
  modalClasses?: ModalProps['classes'];
  /** Extra class name applied to the modal content wrapper */
  modalClassName?: string;
  /** Whether the lesson content editor has content */
  hasLessonContent: boolean;
  /** Currently uploaded slide deck URL */
  slideDeckUrl?: string | null;
  /** Currently uploaded slide deck file name */
  slideDeckFileName?: string | null;
  /** Callback when a slide deck upload is completed */
  onSlideDeckUpload: (input: { url: string; fileName: string | null }) => void;
  /** Callback to remove the uploaded slide deck */
  onSlideDeckRemove?: () => void;
}

const GenerateVideoModal = (props: GenerateVideoModalProps) => {
  const {
    blockId,
    videoScript,
    onChangeVideoScript,
    onConfigChange,
    onGenerateVideoScript,
    onRegenerateVideoScript,
    generatedVideoScript,
    isGeneratingVideoScript,
    isSelectingSlides,
    onClose,
    onGenerateStoryboard,
    initialConfigValues,
    shouldAutoOpenScriptPrompt = false,
    modalClasses,
    modalClassName,
    hasLessonContent,
    slideDeckUrl,
    slideDeckFileName,
    onSlideDeckUpload,
    onSlideDeckRemove,
  } = props;
  const { t } = useTranslation('Course');
  const [formikSubmit, setFormikSubmit] = useState<any>();
  const [formValues, setFormValues] = useState<GenerateVideoFormValues>(() =>
    resolveGenerateVideoFormValues(initialConfigValues),
  );
  const [isSelectingAvatar, setIsSelectingAvatar] = useState(false);
  const isScriptEmpty = !videoScript;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scriptPreviewRef = useRef<HTMLDivElement>(null);
  const scriptPreviewWhileGenerating =
    generatedVideoScript && generatedVideoScript.length > 0
      ? generatedVideoScript
      : videoScript || '';

  // MUI's TextareaAutosize sets overflow: hidden via inline style after every resize.
  // Override it after each commit so the textarea scrolls within its container.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.overflow = 'auto';
  });

  // Auto-scroll the streaming preview to bottom as new content arrives.
  useLayoutEffect(() => {
    const el = scriptPreviewRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [scriptPreviewWhileGenerating]);

  const handleVideoScriptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChangeVideoScript(e.target.value);
    },
    [onChangeVideoScript],
  );

  const handleFormValuesChange = useCallback(
    (nextValues: GenerateVideoFormValues) => {
      setFormValues((prevValues) => {
        const normalizedAvatarId = nextValues.videoType === 'voiceOver' ? '' : nextValues.avatar;
        const hasAvatarChanged = prevValues.avatar !== nextValues.avatar;
        const hasVoiceChanged = prevValues.voice !== nextValues.voice;
        const hasVideoTypeChanged = prevValues.videoType !== nextValues.videoType;

        if (hasAvatarChanged || hasVoiceChanged || hasVideoTypeChanged) {
          onConfigChange({
            avatarId: normalizedAvatarId,
            voiceId: nextValues.voice,
            videoType: nextValues.videoType,
          });
        }

        return nextValues;
      });
    },
    [onConfigChange],
  );

  return (
    <Modal
      action={{
        text: t('Course:AiVideoGeneration.GenerateStoryboardButtonText'),
        className: 'border-t border-border',
        onSubmit: formikSubmit,
        isSubmitting: isSelectingSlides,
        disabled: isGeneratingVideoScript || !(videoScript ?? generatedVideoScript)?.trim(),
      }}
      className={modalClassName}
      classes={modalClasses}
      header={{
        title: t('Course:AiVideoGeneration.CreateAIVideo'),
        className: 'border-b border-border',
      }}
      variant={ModalVariant.ExtraWide}
      onClose={onClose}>
      <Stack className="h-full flex-1 shrink overflow-hidden" direction="row">
        {/* ── Left panel ── */}
        <Stack
          className="flex-1 shrink min-w-0 min-h-0 px-24 py-24 overflow-y-auto"
          direction="col"
          gap={24}>
          {/* ── Your Slide Deck ── */}
          <Stack className="shrink-0" direction="col" gap={16}>
            {/* Label + description */}
            <Stack className="pl-8" direction="col" gap={4}>
              <Stack alignItems="center" direction="row" gap={10}>
                <Typography
                  className="font-medium leading-[22px]"
                  color="text-text-primary"
                  variant="body-short-01">
                  {t('Course:AiVideoGeneration.YourSlideDeck')}
                </Typography>
                <Typography
                  className="font-medium leading-[22px]"
                  color="text-text-placeholder"
                  variant="body-short-01">
                  ({t('Course:AiVideoGeneration.Optional')})
                </Typography>
              </Stack>
              <Typography
                className="leading-[22px]"
                color="text-text-placeholder"
                variant="body-short-02">
                {t('Course:AiVideoGeneration.SlideDeckDescription')}
              </Typography>
            </Stack>

            {/* Upload area */}
            <SlideDeckUpload
              blockId={blockId}
              slideDeckFileName={slideDeckFileName}
              slideDeckUrl={slideDeckUrl}
              onRemove={onSlideDeckRemove}
              onUpload={onSlideDeckUpload}
            />
          </Stack>

          {/* ── Script ── */}
          <Stack className="shrink-0" direction="col" gap={16}>
            {/* Label row */}
            <Stack
              alignItems="center"
              className="h-[22px] pl-[8px] shrink-0"
              direction="row"
              justifyContent="between">
              <Typography
                className="leading-[22px] font-medium"
                color="text-text-primary"
                variant="body-short-01">
                {t('Course:AiVideoGeneration.Script')}
              </Typography>
              {/* Regenerate button — only when script exists */}
              {!isScriptEmpty ? (
                <RegenerationWindow
                  buttonText={t('Course:AiVideoGeneration.RegenerateScript')}
                  heading={t('Course:AiVideoGeneration.RegenerateScriptHeading')}
                  position="left-start"
                  onClick={onRegenerateVideoScript}>
                  <Tooltip
                    placement="top"
                    title={t('Course:AiVideoGeneration.RegenerateVideoScriptUsingLyearnAI')}>
                    <Button
                      StartIcon={StarAiFill}
                      className={cn('end-0 top-1/2 -translate-y-1/2', styleAsIconButton)}
                      loading={isGeneratingVideoScript}
                      size="x-small"
                      variant="ghost"
                    />
                  </Tooltip>
                </RegenerationWindow>
              ) : (
                /* Loading spinner placeholder — opacity-0 when not generating, matching Figma layout */
                <Box className={cn('h-[24px] w-[24px]', !isGeneratingVideoScript && 'opacity-0')}>
                  {isGeneratingVideoScript && (
                    <StarAiFill
                      className="animate-spin text-text-placeholder"
                      height={24}
                      width={24}
                    />
                  )}
                </Box>
              )}
            </Stack>

            {/* Script content area */}
            <Stack direction="col">
              {isGeneratingVideoScript ? (
                /* Streaming preview */
                <Box
                  ref={scriptPreviewRef}
                  className="w-full h-[360px] px-12 pt-10 border border-border rounded-[8px] bg-bg-primary overflow-y-auto">
                  <Box className="pb-10">
                    <Typography
                      className="whitespace-pre-wrap inline"
                      color="text-text-secondary"
                      variant="body-short-01">
                      {scriptPreviewWhileGenerating}
                      <Typer />
                    </Typography>
                  </Box>
                </Box>
              ) : isScriptEmpty ? (
                /* Empty state — exact Figma layout */
                <Stack
                  alignItems="center"
                  className="h-[360px] rounded-[8px] border border-field bg-bg-secondary p-[24px]"
                  direction="col"
                  justifyContent="center">
                  <Stack
                    alignItems="center"
                    className="w-full"
                    direction="col"
                    gap={20}
                    justifyContent="center">
                    {/* Icon + text group */}
                    <Stack alignItems="center" direction="col" gap={8}>
                      <NucleoFeedbackDialogueThinkSpeech
                        className="text-text-placeholder"
                        height={32}
                        width={32}
                      />
                      <Stack
                        alignItems="center"
                        className="text-center w-full"
                        direction="col"
                        gap={2}>
                        <Typography
                          className="font-medium leading-[24px] w-full"
                          color="text-text-primary"
                          variant="body-short-01">
                          {t('Course:AiVideoGeneration.VideoScript')}
                        </Typography>
                        <Typography
                          className="leading-[22px]"
                          color="text-text-placeholder"
                          variant="body-short-02">
                          {t('Course:AiVideoGeneration.GenerateScriptFromLessonContent')}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Generate Script button */}
                    {hasLessonContent || slideDeckUrl ? (
                      <Button
                        EndIcon={StarAiFill}
                        className="!rounded-full !bg-black !text-white"
                        size="small"
                        onClick={() => onGenerateVideoScript()}>
                        {t('Course:AiVideoGeneration.GenerateScript')}
                      </Button>
                    ) : (
                      <GenerateScriptWindow
                        defaultOpen={shouldAutoOpenScriptPrompt && !isGeneratingVideoScript}
                        isLoading={isGeneratingVideoScript}
                        position="bottom"
                        onClick={onGenerateVideoScript}>
                        {/* Popover.Button uses cloneElement — Button is the real interactive element */}
                        <Button
                          EndIcon={StarAiFill}
                          className="!rounded-full !bg-black !text-white"
                          size="small">
                          {t('Course:AiVideoGeneration.GenerateScript')}
                        </Button>
                      </GenerateScriptWindow>
                    )}
                  </Stack>
                </Stack>
              ) : (
                /* Editable textarea */
                <Textarea
                  className="w-full pb-10 h-[360px]"
                  inputRef={textareaRef}
                  maxRows={13}
                  minRows={13}
                  value={videoScript ?? generatedVideoScript}
                  onChange={handleVideoScriptChange}
                />
              )}
            </Stack>
          </Stack>
        </Stack>

        {/* ── Right panel – Video settings ── */}
        <Stack
          className="max-w-[58rem] w-[48%] min-w-[580px] min-h-0 overflow-y-auto px-24 py-24 border-l border-border"
          direction="col"
          gap={24}>
          {!isSelectingAvatar ? (
            <Typography variant="heading-s">
              {t('Course:AiVideoGeneration.VideoSettings')}
            </Typography>
          ) : null}
          <GenerateVideoForm
            initialValues={formValues}
            setSubmitRef={setFormikSubmit}
            onAvatarSelectionChange={setIsSelectingAvatar}
            onSubmit={onGenerateStoryboard}
            onValuesChange={handleFormValuesChange}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};

export default GenerateVideoModal;
